import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import { fmtDateTime } from '../../../../utils/region';
import Pagination, { getPageSize } from '../../../Pagination';
import PeakToolbar, { type BarAction } from '../../../PeakToolbar';
import DlBtn from '../../../DlBtn';
import { baht, btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pill, pillAmber, pillGreen, pillGrey, pillRed, table, td, th } from '../../ui';
import { Loader, Stat, actBtn, emptyLine } from '../../dialog';
import { STATUS_KEY, useFaParams } from './shared';
import type { FaAsset, FaCategory, FaPage, FaStatus, TabProps } from './shared';
import { AssetWindow } from './AssetWindow';
import { AssetForm } from './AssetForm';
import { ImportWindow } from './ImportWindow';
import { ReconWindow } from './ReconWindow';

// ── Accounting › Fixed assets › Asset Register (batch 9 R4 — COA-H1 tab 2, H4 list, H12, H14, H11/H3) ──
// The register in the shape of the customer's reference list: a search, the computed-status / category /
// purchase-date filters, Export of exactly what is showing, the statutory PDF, four summary cards and a
// server-sorted, server-paged table (100 a page, CLAUDE.md). This file lays the rows out and derives
// nothing: the status is the server's COMPUTED one, the cost the capitalised total, the cards the
// server's summary of what the filters show. Every record opens full screen over the list (COA-H16), so
// closing it returns to the list exactly as it was — the filters, page and sort are this component's
// state and nothing here unmounts while a window is open.

type SortKey = 'asset_no' | 'name' | 'category' | 'start' | 'cost' | 'accumulated' | 'nbv' | 'status';
const AMOUNT_SORTS: SortKey[] = ['cost', 'accumulated', 'nbv'];
/** What a register page carries beyond the shared row (services/fixedAssets.ts shapeAsset / listAssets):
 *  the book value an asset had when it left (its `nbv` is then 0), who removed a removed one and when,
 *  and how many of the filtered rows are still HELD — the three money cards are summed over those. */
type RegRow = FaAsset & { nbv_at_disposal?: number | null; deleted?: number; deleted_by_name?: string; deleted_at?: number | null };
type RegPage = Omit<FaPage, 'rows' | 'summary'> & { rows: RegRow[]; summary: FaPage['summary'] & { held: number } };
/** '' = what is still held (the server's default), 'all', each of the six statuses, then 'removed' —
 *  the assets taken off the register as registered by mistake, which only that choice lists (CLAUDE.md:
 *  a deleted row stays, marked). The server never mixes them into the default or 'all'. */
const STATUS_FILTERS: string[] = ['', 'all', ...Object.keys(STATUS_KEY), 'removed'];
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
const STATUS_PILL: Record<FaStatus, React.CSSProperties> = {
  in_use: pillGreen, not_yet_available: pillAmber, idle: pillGrey, fully_depreciated: pillBlue, disposed: pillGrey, written_off: pillRed,
};
/** The words the server stores in `overrides` (services/fixedAssets.ts overridesOf), each key in full. */
const OVERRIDE_KEY: Record<string, string> = {
  rate: 'aw_far_ovr_rate', method: 'aw_far_ovr_method', assetAccount: 'aw_far_ovr_asset_account',
  accumAccount: 'aw_far_ovr_accum_account', expenseAccount: 'aw_far_ovr_expense_account',
};
/** The day depreciation starts, as the server's chargeFrom reads it. */
const startOf = (a: FaAsset) => a.depreciation_start || a.available_date || a.acquired_date;
const divider = <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />;
const miniLbl: React.CSSProperties = { fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' };

export function RegisterTab({ ctx, canWrite }: TabProps) {
  const { t, i18n } = useTranslation();
  const fp = useFaParams();
  const th_ = i18n.language === 'th';
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<SortKey>('asset_no');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<RegPage | null>(null);
  const [err, setErr] = useState('');
  const [cats, setCats] = useState<FaCategory[] | null>(null);
  const [catErr, setCatErr] = useState('');
  const [importing, setImporting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [pdfErr, setPdfErr] = useState('');

  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.faAssetsPage(ctx.clientId, { q: qd, status, category, from, to, sort, dir, page, limit })
      .then((x: RegPage) => { if (alive) { setData(x); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit, summary: { count: 0, held: 0, cost: 0, accumulated: 0, nbv: 0 }, storedStatuses: [], methods: [] }); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, qd, status, category, from, to, sort, dir, page, limit]);
  // Every category, switched-off ones too: an asset may sit in one, and the filter must be able to name it.
  // ⛔ A failed read is NOT "this client has no category" (review R4 #26): that turned a 500 or a dropped
  // connection into the "assets need a category first" card and took Add / Import away from a client
  // that has both. The error is shown; the list stays what it last was (null before the first answer,
  // so the empty-state card waits for a real answer); a later successful read clears the error.
  useEffect(() => { setCats(null); setCatErr(''); }, [ctx.clientId]);
  useEffect(() => {
    let alive = true;
    api.faCategories(ctx.clientId, true)
      .then((x: any) => { if (alive) { setCats((x?.categories || []) as FaCategory[]); setCatErr(''); } })
      .catch((e: any) => { if (alive) setCatErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);

  // After any write: the shell's counter, which this list (and every other pane) re-reads on.
  const reload = () => ctx.bump();
  const hasCategory = (cats || []).some(c => Number(c.active));
  const catName = (c: { name_th: string; name_en: string }) => (th_ ? c.name_th || c.name_en : c.name_en || c.name_th);
  const rows = data?.rows || [];
  const sum = data?.summary;
  const removedView = status === 'removed';
  const filtered = !!(q || status || category || from || to);
  const clearAll = () => { setQ(''); setQd(''); setStatus(''); setCategory(''); setFrom(''); setTo(''); setPage(1); };
  const sortBy = (k: SortKey) => {
    if (k === sort) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(k); setDir(AMOUNT_SORTS.includes(k) ? 'desc' : 'asc'); }
    setPage(1);
  };
  /** The reconciliation names assets by NUMBER; the window opens by id. The search is a substring
   *  match, so the exact number is picked out of what it returns — every status, a disposed one too. */
  const openByNo = async (assetNo: string) => {
    try {
      const x: FaPage = await api.faAssetsPage(ctx.clientId, { q: assetNo, status: 'all', limit: 200 });
      const hit = (x.rows || []).find(r => r.asset_no === assetNo);
      if (hit) fp.open(hit.id); else setNotice(t('aw_far_not_found', { no: assetNo }));
    } catch (e: any) { setNotice(String(e?.message || e)); }
  };

  // Plain render helpers rather than components declared in here: a component type made afresh on
  // every render would remount its header (and drop focus) each time the list re-reads.
  const sortBtn = (k: SortKey, label: string) => (
    <button type="button" data-sort={k} onClick={() => sortBy(k)} title={t('aw_far_sort_title')}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: k === sort ? 'var(--accent-text)' : 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}>
      {label}{k === sort ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
  const ariaSort = (ks: SortKey[]) => (ks.includes(sort) ? (dir === 'asc' ? 'ascending' : 'descending') : undefined);
  const sortTh = (k: SortKey, label: string, right = false) => (
    <th style={{ ...th, textAlign: right ? 'right' : 'left' }} aria-sort={ariaSort([k])}>{sortBtn(k, label)}</th>
  );

  const filters = (
    <>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_far_search_ph')} aria-label={t('aw_far_search_ph')} style={{ ...inp, flex: 'none', width: 260 }} />
      {divider}
      <label style={miniLbl}>
        {t('aw_far_status_filter')}{' '}
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} aria-label={t('aw_far_status_filter')} data-fa-filter="status" style={{ ...inp, padding: '5px 8px' }}>
          {STATUS_FILTERS.map(s => (
            <option key={s || 'held'} value={s}>{s === '' ? t('aw_far_status_held') : s === 'all' ? t('aw_far_status_all') : s === 'removed' ? t('aw_far_status_removed') : t(STATUS_KEY[s as FaStatus])}</option>
          ))}
        </select>
      </label>
      <label style={miniLbl}>
        {t('aw_far_cat_filter')}{' '}
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} aria-label={t('aw_far_cat_filter')} data-fa-filter="category" style={{ ...inp, padding: '5px 8px', maxWidth: 220 }}>
          <option value="">{t('aw_far_cat_all')}</option>
          {(cats || []).map(c => <option key={c.id} value={c.id}>{catName(c)}</option>)}
          <option value="-">{t('aw_far_cat_none')}</option>
        </select>
      </label>
      {divider}
      <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_far_from')} aria-label={t('aw_far_from')} style={{ ...inp, flex: 'none' }} />
      <span style={{ color: 'var(--text-tertiary)' }}>–</span>
      <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_far_to')} aria-label={t('aw_far_to')} style={{ ...inp, flex: 'none' }} />
      {filtered && (
        <button type="button" onClick={clearAll} title={t('aw_far_clear')} aria-label={t('aw_far_clear')} style={{ ...actBtn, padding: '4px 7px', flex: 'none' }}>
          <i className="ti ti-x" aria-hidden />
        </button>
      )}
    </>
  );
  // ⛔ The sheet answers the question the screen is showing: the same filters and the same sort.
  // ⛔ The house toolbar never wraps (PeakToolbar): what sits in `right` never folds, so only the two
  // things a reader comes for stay there — the sheet of this list and, for a writer, the new asset.
  // The rest are `actions`, which fold into the bar's More menu when the filters need the width;
  // with all five in `right` the category and date filters were pushed off the row at 1440px.
  const right = (
    <>
      <DlBtn style={btn} title={t('aw_export_title')}
        onRun={() => api.faAssetsExport(ctx.clientId, { q: qd, status, category, from, to, sort, dir })}>
        <i className="ti ti-file-spreadsheet" aria-hidden /> {t('aw_export')}
      </DlBtn>
      {/* ⛔ Writes are left OUT for a read-only seat or a closed period, never shown disabled — and with
          no active category there is nothing an asset could be registered under (COA-H3). */}
      {canWrite && hasCategory && (
        <button type="button" onClick={() => fp.edit('new')} title={t('aw_far_add_title')} style={btnPrimary}>
          <i className="ti ti-plus" aria-hidden /> {t('aw_far_add')}
        </button>
      )}
    </>
  );
  const openPdf = () => {
    if (pdfBusy) return;
    setPdfErr(''); setPdfBusy(true);
    api.faRegisterUrl(ctx.periodId).then(openBlob).catch((e: any) => setPdfErr(String(e?.message || e))).finally(() => setPdfBusy(false));
  };
  const actions: BarAction[] = [
    { key: 'recon', label: t('aw_far_recon_btn'), icon: 'ti-scale', title: t('aw_far_recon_btn_title'), onClick: () => setReconOpen(true) },
    { key: 'pdf', label: t('aw_far_register_pdf'), icon: 'ti-file-type-pdf', title: t('aw_far_register_pdf_title'), busy: pdfBusy, onClick: openPdf },
    ...(canWrite && hasCategory ? [{ key: 'import', label: t('aw_far_import'), icon: 'ti-file-import', title: t('aw_far_import_title'), onClick: () => setImporting(true) }] : []),
  ];

  // The Expense record window sends the operator here with ?fasrc=<record> and no ?faedit= — the
  // create form, bought on that record.
  const formOpen = !!(fp.editId || fp.src);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {cats !== null && !hasCategory && (
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'flex-start' }} data-fa-empty="categories">
          <i className="ti ti-category" style={{ fontSize: 20, color: 'var(--text-secondary)', flexShrink: 0, marginTop: 1 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>{t('aw_far_nocat_title')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>{t('aw_far_nocat_body')}</div>
          </div>
          {canWrite && (
            <button type="button" onClick={() => fp.setTab('categories')} style={{ ...btnPrimary, flex: 'none' }}>
              <i className="ti ti-category-plus" aria-hidden /> {t('aw_far_nocat_btn')}
            </button>
          )}
        </div>
      )}
      {/* ⛔ The money cards are the server's sums over the assets still HELD among the rows (review R4
          #18/#20), so Cost − Accumulated = Net book value under every filter; where some rows have left,
          the count says how many of them those amounts are. A removed asset is in no figure at all, so
          the removed view shows its count alone. */}
      {sum && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-fa-summary="1">
          <Stat label={t('aw_far_sum_assets')} value={<span data-fa-sum="count">{sum.count}</span>}
            sub={removedView ? t('aw_far_sum_removed_sub') : Number(sum.held) !== Number(sum.count) ? t('aw_far_sum_held', { n: Number(sum.held) || 0 }) : t('aw_far_sum_sub')} />
          {!removedView && (
            <>
              <Stat label={t('aw_far_sum_cost')} value={<span data-fa-sum="cost">{baht(sum.cost)}</span>} />
              <Stat label={t('aw_far_sum_accum')} value={<span data-fa-sum="accumulated">{baht(sum.accumulated)}</span>} />
              <Stat label={t('aw_far_sum_nbv')} value={<span data-fa-sum="nbv">{baht(sum.nbv)}</span>} />
            </>
          )}
        </div>
      )}
      {notice && (
        <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-fa-notice="1">
          <i className="ti ti-info-circle" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
          <div style={{ flex: 1 }}>{notice}</div>
          <button type="button" onClick={() => setNotice('')} aria-label={t('aw_dlg_close')} style={{ ...actBtn, padding: '2px 6px' }}><i className="ti ti-x" aria-hidden /></button>
        </div>
      )}
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        <PeakToolbar filters={filters} right={right} actions={actions} />
        {err && <div style={{ ...errBox, margin: 12 }}>{err}</div>}
        {catErr && <div style={{ ...errBox, margin: 12 }} data-fa-cat-err="1">{t('aw_far_cat_err', { why: catErr })}</div>}
        {pdfErr && <div style={{ ...errBox, margin: 12 }}>{pdfErr}</div>}
        <div style={{ overflowX: 'auto' }}>
          {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_far_none')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  {/* One column, two sorts: the customer's "Asset Name/Code" heading sorts by either. */}
                  <th style={th} aria-sort={ariaSort(['asset_no', 'name'])}>
                    {sortBtn('asset_no', t('aw_far_col_no'))} <span aria-hidden>&amp;</span> {sortBtn('name', t('aw_far_col_name'))}
                  </th>
                  {sortTh('category', t('aw_far_col_category'))}
                  {sortTh('start', t('aw_far_col_start'))}
                  {sortTh('cost', t('aw_far_col_cost'), true)}
                  {sortTh('accumulated', t('aw_far_col_accum'), true)}
                  {sortTh('nbv', t('aw_far_col_nbv'), true)}
                  {sortTh('status', t('aw_far_col_status'))}
                  <th style={th}>{t('aw_far_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const gone = r.status === 'disposed' || r.status === 'written_off';
                  // Removed (review R4 #17): kept on the list, struck through, with who removed it and when;
                  // it opens like any other row — the asset window shows it read-only.
                  const removed = Number(r.deleted) === 1;
                  const struck: React.CSSProperties = removed ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : {};
                  const removedBy = [r.deleted_by_name || '', fmtDateTime(r.deleted_at)].filter(Boolean).join(' · ');
                  const ovr = (r.overrides || []).map(o => (OVERRIDE_KEY[o] ? t(OVERRIDE_KEY[o]) : o));
                  return (
                    <tr key={r.id} data-fa-id={r.id} data-fa-no={r.asset_no} data-fa-removed={removed ? '1' : undefined} onClick={() => fp.open(r.id)} title={t('aw_far_open_title')}
                      style={{ cursor: 'pointer', opacity: gone ? 0.6 : 1 }}>
                      <td style={{ ...td, ...struck }}>
                        <div style={{ fontWeight: 700, color: removed ? 'inherit' : 'var(--accent)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.asset_no}</div>
                        <div>{r.name_th}</div>
                        {r.name_en && r.name_en !== r.name_th && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.name_en}</div>}
                      </td>
                      <td style={{ ...td, ...struck }}>
                        {r.category_id
                          ? <span>{th_ ? r.category_th || r.category_en : r.category_en || r.category_th}</span>
                          : <span style={{ color: 'var(--text-tertiary)' }} title={t('aw_far_no_category_title')}>—</span>}
                        <span style={{ display: 'inline-flex', gap: 5, marginLeft: 6, color: 'var(--text-tertiary)', verticalAlign: 'middle' }}>
                          {ovr.length > 0 && <i className="ti ti-adjustments" data-fa-mark="override" title={t('aw_far_overrides_title', { list: ovr.join(', ') })} aria-label={t('aw_far_overrides_title', { list: ovr.join(', ') })} style={{ color: '#92400E' }} />}
                          {Number(r.files) > 0 && <i className="ti ti-paperclip" data-fa-mark="files" title={t('aw_far_files_title', { n: r.files })} aria-label={t('aw_far_files_title', { n: r.files })} />}
                          {r.source_key && <i className="ti ti-link" data-fa-mark="source" title={t('aw_far_source_title')} aria-label={t('aw_far_source_title')} />}
                        </span>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', ...struck }} title={t('aw_far_start_title')}>{startOf(r) ? dmy(startOf(r)) : '—'}</td>
                      <td style={{ ...num, ...struck }} data-fa-col="cost">{baht(r.capitalised_total)}</td>
                      <td style={{ ...num, ...struck }} data-fa-col="accumulated">{baht(r.accumulated)}</td>
                      {/* ⛔ An asset that has left carries no book value (review R4 #18): a dash, as the cards
                          and the note say, never the amount it was carried at — that is the hover. */}
                      <td style={{ ...num, fontWeight: 700, ...(gone ? { color: 'var(--text-tertiary)' } : {}), ...struck }} data-fa-col="nbv"
                        title={gone && r.nbv_at_disposal != null ? t('aw_far_nbv_gone_title', { amount: baht(r.nbv_at_disposal) }) : undefined}>
                        {gone ? '—' : baht(r.nbv)}
                      </td>
                      <td style={td}>
                        {removed ? (
                          <>
                            <span style={pillRed} data-fa-status="removed">{t('aw_far_status_removed')}</span>
                            {removedBy && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3, whiteSpace: 'nowrap' }} data-fa-removed-by="1">{t('aw_far_removed_by', { who: removedBy })}</div>}
                          </>
                        ) : <span style={STATUS_PILL[r.status] || pillGrey} data-fa-status={r.status}>{STATUS_KEY[r.status] ? t(STATUS_KEY[r.status]) : r.status}</span>}
                      </td>
                      {/* The keyboard's way in (review R4 #23): the row's click is the mouse's; this button is
                          focusable, and stops the click there so the row does not open it a second time. */}
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button type="button" data-fa-open={r.id} onClick={e => { e.stopPropagation(); fp.open(r.id); }}
                          title={t('aw_far_open_title')} aria-label={t('aw_far_rec_open_asset', { no: r.asset_no })}
                          style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
                          <i className="ti ti-arrows-maximize" aria-hidden /> {t('aw_far_open')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note} data-fa-note="1">{removedView ? t('aw_far_removed_note') : t('aw_far_note')}</div>

      {/* The windows, in the order they stack: the reconciliation can open an asset over itself, and the
          asset window can open its edit form over itself. */}
      {reconOpen && <ReconWindow ctx={ctx} onClose={() => setReconOpen(false)} onOpenAsset={no => { void openByNo(no); }} />}
      {importing && <ImportWindow ctx={ctx} onClose={() => setImporting(false)} onDone={() => { setImporting(false); setNotice(t('aw_far_imported')); reload(); }} />}
      {fp.openId && <AssetWindow ctx={ctx} canWrite={canWrite} assetId={fp.openId} onClose={fp.close} onEdit={id => fp.edit(id)} onChanged={reload} />}
      {/* ⛔ onSaved is ONE URL write: react-router's setSearchParams reads the params of the render it was
          made in, so endEdit() then open() back to back would let the second undo the first. */}
      {formOpen && (
        <AssetForm ctx={ctx} assetId={fp.editId === 'new' ? '' : fp.editId} sourceKey={fp.src} onClose={fp.endEdit}
          onSaved={id => { fp.set({ faedit: '', fasrc: '', fa: id }); reload(); }} />
      )}
    </div>
  );
}
